import React, { useState, useEffect } from 'react';
import { colors, fonts, spacing, radii, shadows } from '../styles/theme';
import { fetchFeedbackList, fetchFeedbackStats, markFeedbackRead } from '../services/api';

interface FeedbackViewerProps {
    onBack: () => void;
}

const RADAR_LABELS: Record<string, string> = {
    clarity: 'Clarity',
    relevance: 'Relevance',
    completeness: 'Completeness',
    navigation: 'Navigation',
    visual_design: 'Visual Design',
    engagement: 'Engagement',
};

const TRIPLE_LABELS: Record<string, string[]> = {
    depth:             ['Too shallow', 'Just right', 'Too deep'],
    shopping_metaphor: ["Didn't help", 'Somewhat', 'Definitely!'],
    session_length:    ['Too short', 'Just right', 'Too long'],
};

const STAT_KEYS = [
    { key: 'avg_clarity', label: 'Clarity', max: 5 },
    { key: 'avg_relevance', label: 'Relevance', max: 5 },
    { key: 'avg_completeness', label: 'Completeness', max: 5 },
    { key: 'avg_navigation', label: 'Navigation', max: 5 },
    { key: 'avg_visual_design', label: 'Visual design', max: 5 },
    { key: 'avg_engagement', label: 'Engagement', max: 5 },
    { key: 'avg_nps', label: 'NPS', max: 10 },
];

function describeAffectGrid(x: number | null, y: number | null): string {
    if (x == null || y == null) return '–';
    const valence = x >= 60 ? 'Pleasant' : x >= 40 ? 'Neutral' : 'Unpleasant';
    const arousal = y >= 60 ? 'Activated' : y >= 40 ? 'Moderate' : 'Calm';
    return `${valence} & ${arousal} (${x}%, ${y}%)`;
}

// Mini affect grid dot for response cards
const MiniGrid: React.FC<{ x: number; y: number }> = ({ x, y }) => (
    <div style={{
        position: 'relative', width: 48, height: 48, borderRadius: 6,
        border: `1px solid ${colors.border}`, flexShrink: 0,
        background: `
            radial-gradient(ellipse at top right, #5a9e6f12, transparent 70%),
            radial-gradient(ellipse at bottom left, #c95d5d10, transparent 70%),
            ${colors.bgLight}`,
    }}>
        <div style={{
            position: 'absolute', left: `${x}%`, bottom: `${y}%`,
            transform: 'translate(-50%, 50%)',
            width: 8, height: 8, borderRadius: '50%',
            backgroundColor: colors.primary, border: '1.5px solid white',
        }} />
    </div>
);

const FeedbackViewer: React.FC<FeedbackViewerProps> = ({ onBack }) => {
    const [feedback, setFeedback] = useState<any[]>([]);
    const [stats, setStats] = useState<Record<string, number> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const [authed, setAuthed] = useState(localStorage.getItem('indicatorAdminAuth') === 'ok');
    const [user, setUser] = useState('');
    const [pass, setPass] = useState('');

    const handleLogin = () => {
        const envUser = process.env.REACT_APP_ADMIN_USER || '';
        const envPass = process.env.REACT_APP_ADMIN_PASS || '';
        if (user === envUser && pass === envPass) {
            localStorage.setItem('indicatorAdminAuth', 'ok');
            setAuthed(true);
        } else {
            setError('Invalid credentials');
        }
    };

    useEffect(() => {
        if (!authed) return;
        const load = async () => {
            setLoading(true);
            try {
                const [fb, st] = await Promise.all([fetchFeedbackList(), fetchFeedbackStats()]);
                setFeedback(fb.feedback);
                setStats(st.stats);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load feedback');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [authed]);

    const handleExpand = async (id: number, isRead: boolean) => {
        setExpandedId(expandedId === id ? null : id);
        if (!isRead) {
            try {
                await markFeedbackRead(id);
                setFeedback(prev => prev.map(f => (f.id === id ? { ...f, is_read: 1 } : f)));
                if (stats) setStats({ ...stats, unread: Math.max(0, (stats.unread || 0) - 1) });
            } catch { /* ignore */ }
        }
    };

    if (!authed) {
        return (
            <div style={styles.page}>
                <div style={styles.centerCard}>
                    <h2 style={styles.heading}>Feedback Viewer</h2>
                    <p style={styles.subtitle}>Admin access required</p>
                    <div style={styles.fieldGroup}>
                        <input type="text" placeholder="Username" value={user}
                            onChange={e => setUser(e.target.value)} style={styles.input} />
                        <input type="password" placeholder="Password" value={pass}
                            onChange={e => setPass(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleLogin()} style={styles.input} />
                        <button onClick={handleLogin} style={styles.primaryButton}>Login</button>
                    </div>
                    {error && <p style={styles.error}>{error}</p>}
                    <button onClick={onBack} style={styles.backButton}>← Back</button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.wrapper}>
                <button onClick={onBack} style={styles.backButton}>← Back to home</button>
                <h1 style={styles.heading}>Feedback</h1>

                {loading && <p style={styles.subtitle}>Loading...</p>}
                {error && <p style={styles.error}>{error}</p>}

                {/* ── Summary Stats ──────────────────────────── */}
                {stats && (
                    <>
                        <div style={styles.statsGrid}>
                            <div style={styles.statCard}>
                                <span style={styles.statValue}>{stats.total || 0}</span>
                                <span style={styles.statLabel}>Responses</span>
                            </div>
                            <div style={{ ...styles.statCard, borderColor: stats.unread ? colors.primary : colors.border }}>
                                <span style={{ ...styles.statValue, color: stats.unread ? colors.primary : colors.text }}>
                                    {stats.unread || 0}
                                </span>
                                <span style={styles.statLabel}>Unread</span>
                            </div>
                            <div style={styles.statCard}>
                                <span style={styles.statValue}>
                                    {stats.avg_valence != null ? `${stats.avg_valence}%` : '–'}
                                </span>
                                <span style={styles.statLabel}>Avg valence</span>
                            </div>
                            <div style={styles.statCard}>
                                <span style={styles.statValue}>
                                    {stats.avg_arousal != null ? `${stats.avg_arousal}%` : '–'}
                                </span>
                                <span style={styles.statLabel}>Avg arousal</span>
                            </div>
                            {STAT_KEYS.map(({ key, label, max }) => (
                                <div key={key} style={styles.statCard}>
                                    <span style={styles.statValue}>
                                        {stats[key] != null ? `${stats[key]}/${max}` : '–'}
                                    </span>
                                    <span style={styles.statLabel}>{label}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* ── Individual Responses ───────────────────── */}
                <h2 style={{ ...styles.heading, fontSize: fonts.lg, marginTop: spacing[6] }}>
                    All responses ({feedback.length})
                </h2>

                {feedback.map(fb => {
                    const expanded = expandedId === fb.id;
                    const isUnread = !fb.is_read;
                    return (
                        <div
                            key={fb.id}
                            style={{
                                ...styles.responseCard,
                                borderLeftColor: isUnread ? colors.primary : colors.border,
                                borderLeftWidth: isUnread ? 4 : 1,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => handleExpand(fb.id, !!fb.is_read)}
                                style={styles.responseHeader}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
                                    {fb.overall_valence != null && fb.overall_arousal != null && (
                                        <MiniGrid x={fb.overall_valence} y={fb.overall_arousal} />
                                    )}
                                    <div>
                                        <strong>{fb.expert_name || 'Anonymous'}</strong>
                                        <span style={styles.responseDate}>
                                            {' — '}
                                            {new Date(fb.created_at).toLocaleDateString('en-GB', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </span>
                                        {isUnread && <span style={styles.newBadge}>NEW</span>}
                                    </div>
                                </div>
                                <div style={styles.miniScores}>
                                    <span>NPS: {fb.nps_score ?? '–'}/10</span>
                                </div>
                                <span style={styles.chevron}>{expanded ? '▲' : '▼'}</span>
                            </button>

                            {expanded && (
                                <div style={styles.responseBody}>
                                    {/* Affect grid */}
                                    <div style={styles.detailSection}>
                                        <span style={styles.detailLabel}>Overall feeling (affect grid)</span>
                                        <span style={styles.detailValue}>
                                            {describeAffectGrid(fb.overall_valence, fb.overall_arousal)}
                                        </span>
                                    </div>

                                    {/* Radar dimensions */}
                                    <div style={{ ...styles.detailGrid, marginTop: spacing[3] }}>
                                        {Object.entries(RADAR_LABELS).map(([field, label]) => (
                                            <div key={field} style={styles.detailItem}>
                                                <span style={styles.detailLabel}>{label}</span>
                                                <span style={styles.detailValue}>
                                                    {fb[field] != null ? `${fb[field]}/5` : '–'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Triple choices */}
                                    <div style={{ ...styles.detailGrid, marginTop: spacing[3] }}>
                                        {Object.entries(TRIPLE_LABELS).map(([field, labels]) => (
                                            <div key={field} style={styles.detailItem}>
                                                <span style={styles.detailLabel}>{field.replace(/_/g, ' ')}</span>
                                                <span style={styles.detailValue}>
                                                    {fb[field] != null ? labels[fb[field] - 1] || fb[field] : '–'}
                                                </span>
                                            </div>
                                        ))}
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>NPS score</span>
                                            <span style={styles.detailValue}>{fb.nps_score ?? '–'}/10</span>
                                        </div>
                                    </div>

                                    {fb.top_strengths && (() => {
                                        let tags: string[] = [];
                                        try { tags = typeof fb.top_strengths === 'string' ? JSON.parse(fb.top_strengths) : fb.top_strengths; } catch { /* */ }
                                        return tags.length > 0 ? (
                                            <div style={styles.detailSection}>
                                                <span style={styles.detailLabel}>Top strengths</span>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing[1], marginTop: spacing[1] }}>
                                                    {tags.map((t: string) => <span key={t} style={styles.tag}>{t}</span>)}
                                                </div>
                                            </div>
                                        ) : null;
                                    })()}

                                    {fb.completeness_comment && (
                                        <div style={styles.detailSection}>
                                            <span style={styles.detailLabel}>Coverage gaps</span>
                                            <p style={styles.detailText}>{fb.completeness_comment}</p>
                                        </div>
                                    )}
                                    {fb.improvements && (
                                        <div style={styles.detailSection}>
                                            <span style={styles.detailLabel}>Improvements</span>
                                            <p style={styles.detailText}>{fb.improvements}</p>
                                        </div>
                                    )}
                                    {fb.general_comments && (
                                        <div style={styles.detailSection}>
                                            <span style={styles.detailLabel}>General comments</span>
                                            <p style={styles.detailText}>{fb.general_comments}</p>
                                        </div>
                                    )}
                                    {fb.expert_email && (
                                        <div style={styles.detailSection}>
                                            <span style={styles.detailLabel}>Email</span>
                                            <a href={`mailto:${fb.expert_email}`} style={{ color: colors.primary, fontSize: fonts.sm }}>
                                                {fb.expert_email}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {!loading && feedback.length === 0 && (
                    <p style={styles.subtitle}>No feedback yet. Share the link to collect responses!</p>
                )}
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        backgroundColor: colors.bgLight,
        display: 'flex',
        justifyContent: 'center',
        padding: spacing[6],
    },
    centerCard: {
        maxWidth: 400, width: '100%', margin: '80px auto',
        backgroundColor: colors.white, borderRadius: radii.xl,
        border: `1px solid ${colors.border}`, padding: spacing[8],
        boxShadow: shadows.lg,
    },
    wrapper: { width: '100%', maxWidth: 900 },
    heading: {
        fontSize: fonts['2xl'], fontWeight: fonts.bold as number,
        color: colors.text, marginBottom: spacing[4],
    },
    subtitle: { fontSize: fonts.base, color: colors.textSecondary, marginBottom: spacing[4] },
    fieldGroup: {
        display: 'flex', flexDirection: 'column', gap: spacing[3], marginBottom: spacing[4],
    },
    input: {
        padding: `${spacing[3]} ${spacing[3]}`, fontSize: fonts.base,
        borderRadius: radii.md, border: `1px solid ${colors.border}`,
        outline: 'none', fontFamily: 'inherit',
    },
    primaryButton: {
        padding: `${spacing[3]} ${spacing[5]}`, fontSize: fonts.base,
        fontWeight: fonts.semibold as number, color: colors.white,
        backgroundColor: colors.primary, border: 'none',
        borderRadius: radii.md, cursor: 'pointer',
    },
    backButton: {
        marginBottom: spacing[4], padding: `${spacing[1]} ${spacing[2]}`,
        border: 'none', background: 'none', color: colors.textSecondary,
        fontSize: fonts.sm, cursor: 'pointer', textDecoration: 'underline',
    },
    error: { fontSize: fonts.sm, color: colors.no, marginTop: spacing[2] },
    statsGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
        gap: spacing[3], marginBottom: spacing[4],
    },
    statCard: {
        backgroundColor: colors.white, borderRadius: radii.lg,
        border: `1px solid ${colors.border}`, padding: spacing[3],
        textAlign: 'center' as const,
    },
    statValue: {
        display: 'block', fontSize: fonts.xl, fontWeight: fonts.bold as number, color: colors.text,
    },
    statLabel: {
        display: 'block', fontSize: fonts.xs, color: colors.textMuted,
        marginTop: 2, textTransform: 'capitalize' as const,
    },
    responseCard: {
        backgroundColor: colors.white, borderRadius: radii.lg,
        border: `1px solid ${colors.border}`, marginBottom: spacing[3],
        overflow: 'hidden' as const,
    },
    responseHeader: {
        width: '100%', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: spacing[3],
        padding: `${spacing[3]} ${spacing[4]}`, border: 'none',
        background: 'none', cursor: 'pointer', textAlign: 'left' as const,
        fontSize: fonts.sm, color: colors.text, fontFamily: 'inherit',
    },
    responseDate: { color: colors.textMuted, fontSize: fonts.xs },
    newBadge: {
        display: 'inline-block', marginLeft: spacing[2],
        padding: `1px ${spacing[2]}`, fontSize: '0.65rem',
        fontWeight: fonts.bold as number, color: colors.white,
        backgroundColor: colors.primary, borderRadius: radii.full,
        verticalAlign: 'middle',
    },
    miniScores: {
        display: 'flex', gap: spacing[3], fontSize: fonts.xs,
        color: colors.textSecondary, flexShrink: 0,
    },
    chevron: { fontSize: fonts.xs, color: colors.textMuted },
    responseBody: {
        padding: `${spacing[3]} ${spacing[4]} ${spacing[4]}`,
        borderTop: `1px solid ${colors.borderLight}`,
    },
    detailGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: `${spacing[2]} ${spacing[4]}`,
    },
    detailItem: { display: 'flex', flexDirection: 'column' },
    detailLabel: {
        fontSize: fonts.xs, color: colors.textMuted,
        textTransform: 'capitalize' as const, marginBottom: 2,
    },
    detailValue: { fontSize: fonts.sm, color: colors.text, fontWeight: fonts.medium as number },
    detailSection: {
        marginTop: spacing[3], paddingTop: spacing[3],
        borderTop: `1px solid ${colors.borderLight}`,
    },
    detailText: {
        fontSize: fonts.sm, color: colors.textSecondary, lineHeight: 1.6,
        marginTop: spacing[1], whiteSpace: 'pre-wrap' as const,
    },
    tag: {
        display: 'inline-block', padding: `2px ${spacing[2]}`,
        fontSize: fonts.xs, fontWeight: fonts.medium as number,
        color: colors.primary, backgroundColor: colors.accentLight,
        borderRadius: radii.full,
    },
};

export default FeedbackViewer;
